import React from 'react';
import { Box, Card, CardContent, Divider, Skeleton } from '@mui/material';
import { colors } from '../theme/theme';

export const TabShimmer: React.FC<React.ComponentProps<typeof Skeleton>> = (props) => (
  <Skeleton animation="wave" {...props} />
);

const EventListCardSkeleton: React.FC = () => (
  <Card sx={{ border: `1px solid ${colors.divider}`, borderLeft: `4px solid ${colors.divider}`, borderRadius: 2 }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box display="flex" alignItems="flex-start" gap={2}>
        <TabShimmer variant="rounded" width={56} height={56} sx={{ borderRadius: 2, flexShrink: 0 }} />
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <Box display="flex" alignItems="center" gap={1.5} flex={1} minWidth={0}>
              <TabShimmer variant="text" width="58%" height={28} sx={{ maxWidth: 280 }} />
              <TabShimmer variant="rounded" width={72} height={24} sx={{ borderRadius: 3, flexShrink: 0 }} />
            </Box>
            <TabShimmer variant="circular" width={32} height={32} sx={{ ml: 1, flexShrink: 0 }} />
          </Box>
          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <TabShimmer variant="circular" width={16} height={16} />
            <TabShimmer variant="text" width="48%" height={20} />
          </Box>
          <Box display="flex" gap={3}>
            {[0, 1].map((i) => (
              <Box key={i} display="flex" alignItems="center" gap={1}>
                <TabShimmer variant="circular" width={18} height={18} />
                <Box>
                  <TabShimmer variant="text" width={40} height={14} />
                  <TabShimmer variant="text" width={56} height={22} />
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ClassCardSkeleton: React.FC = () => (
  <Card sx={{ border: `1px solid ${colors.divider}`, borderRadius: 2 }}>
    <TabShimmer variant="rectangular" height={160} sx={{ borderRadius: '8px 8px 0 0' }} />
    <CardContent>
      <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
        <TabShimmer variant="circular" width={40} height={40} />
        <Box flex={1} minWidth={0}>
          <TabShimmer variant="text" width="75%" height={26} />
          <TabShimmer variant="rounded" width={64} height={20} sx={{ borderRadius: 3, mt: 0.5 }} />
        </Box>
        <TabShimmer variant="circular" width={32} height={32} />
      </Box>
      <Box display="flex" flexDirection="column" gap={1}>
        <Box display="flex" justifyContent="space-between">
          <TabShimmer variant="text" width={56} height={16} />
          <TabShimmer variant="text" width={48} height={16} />
        </Box>
        <Box display="flex" justifyContent="space-between">
          <TabShimmer variant="text" width={40} height={16} />
          <TabShimmer variant="text" width={64} height={22} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const InstructorCardSkeleton: React.FC = () => (
  <Card sx={{ border: `1px solid ${colors.divider}`, borderRadius: 2 }}>
    <CardContent>
      <Box display="flex" alignItems="flex-start" gap={2}>
        <TabShimmer variant="circular" width={72} height={72} sx={{ flexShrink: 0 }} />
        <Box flex={1} minWidth={0}>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <TabShimmer variant="text" width="65%" height={26} />
            <TabShimmer variant="circular" width={32} height={32} sx={{ flexShrink: 0 }} />
          </Box>
          <TabShimmer variant="rounded" width={64} height={20} sx={{ borderRadius: 3, mb: 1.5 }} />
          <TabShimmer variant="text" width="55%" height={20} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const SessionCategoryCardSkeleton: React.FC = () => (
  <Card sx={{ border: `1px solid ${colors.divider}`, borderRadius: 2 }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box display="flex" alignItems="center" gap={1.5} mb={2}>
        <TabShimmer variant="rounded" width={44} height={44} sx={{ borderRadius: 2, flexShrink: 0 }} />
        <Box flex={1} minWidth={0}>
          <TabShimmer variant="text" width="70%" height={22} />
          <TabShimmer variant="text" width="50%" height={16} sx={{ mt: 0.5 }} />
        </Box>
      </Box>
      <Divider sx={{ mb: 1.5 }} />
      <Box display="flex" justifyContent="space-between">
        <Box>
          <TabShimmer variant="text" width={36} height={14} />
          <TabShimmer variant="text" width={72} height={20} sx={{ mt: 0.5 }} />
        </Box>
        <Box textAlign="right">
          <TabShimmer variant="text" width={72} height={14} sx={{ ml: 'auto' }} />
          <TabShimmer variant="text" width={48} height={20} sx={{ mt: 0.5, ml: 'auto' }} />
        </Box>
      </Box>
    </CardContent>
  </Card>
);

type GridColumns = { xs?: string; sm?: string; md?: string; lg?: string };

interface TabGridLoadingProps {
  count?: number;
  variant?: 'class' | 'sessionCategory' | 'instructor';
  columns?: GridColumns;
}

export const TabGridLoading: React.FC<TabGridLoadingProps> = ({
  count = 6,
  variant = 'class',
  columns = { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' },
}) => (
  <Box sx={{ display: 'grid', gridTemplateColumns: columns, gap: 2 }}>
    {Array.from({ length: count }).map((_, i) =>
      variant === 'sessionCategory' ? (
        <SessionCategoryCardSkeleton key={i} />
      ) : variant === 'instructor' ? (
        <InstructorCardSkeleton key={i} />
      ) : (
        <ClassCardSkeleton key={i} />
      ),
    )}
  </Box>
);

interface TabListLoadingProps {
  count?: number;
}

export const TabListLoading: React.FC<TabListLoadingProps> = ({ count = 4 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
    {Array.from({ length: count }).map((_, i) => (
      <EventListCardSkeleton key={i} />
    ))}
  </Box>
);
